import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import map from 'lodash/map';
import flatMap from 'lodash/flatMap';
import orderBy from 'lodash/orderBy';
import reduce from 'lodash/reduce';
import groupBy from 'lodash/groupBy';

// types
import { Histogram, ReportInformation } from 'ducks/merchantDucks/reports/types';

// constants
import { PAYMENTS } from 'pages/MerchantPages/Analytics/Payments/PaymentsInfoBox';
import { SUBSCRIPTIONS } from 'pages/MerchantPages/Analytics/Subscriptions/SubscriptionsInfoBox';
import { REVENUE } from 'pages/MerchantPages/Analytics/Revenue/RevenueInfoBox';

// components
import Loader from 'components/Loader';
import SubscriptionsInfoTile from 'pages/MerchantPages/Analytics/Subscriptions/SubscriptionsInfoTile';
import { REGISTERED_CUSTOMERS } from 'pages/MerchantPages/Analytics/Customers/CustomersInfoBox';
import EmptyMessage from 'components/EmptyMessage';
import RegistrationsSection from './RegistrationsSection';
import TopInfo from './TopInfo';
import Tile from './Tile';
import SimpleLineChart, { DataItem } from './charts/LineChart';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  text-align: center;
  margin: 1rem 0;
  height: 100%;
`;

const EmptyMessageStyled = styled(EmptyMessage)`
  margin-top: 2rem;
`;

interface Props {
  interval: string;
  type: number;
  isFetching: boolean;
  reports: Histogram;
  reportInformation: ReportInformation;
}

enum TitleType {
  Subscriptions = 'Subscriptions',
  Payments = 'Payments',
  Audience = 'Audience',
  Revenue = 'Revenue',
}

export default class MainAnalytics extends React.PureComponent<Props> {
  chooseDateFormat = (interval: string) => {
    switch (interval) {
      case 'hour':
        return 'dddd, MMMM Do YYYY h:mm:ss a';
      case 'day':
        return 'dddd, MMMM Do YYYY';
      case 'month':
        return 'MMMM YYYY';
      default:
        return 'LL';
    }
  };

  render() {
    const { interval, type, reports, isFetching, reportInformation } = this.props;

    let newCollection: any = {};
    let data: Array<DataItem> = [];
    let dataKey = '';
    let title = '';
    let dataKeyGroups: string[] = [];
    let groupedByDate: any = {};

    const format = this.chooseDateFormat(interval);

    switch (type) {
      case REGISTERED_CUSTOMERS:
        newCollection = reports.customers ? reports.customers.collection : [];

        if (newCollection.customers) {
          data = newCollection.customers.map((d: { doc_count: number; key: number }) => {
            return {
              name: moment(d.key).utc().format(format),
              customers: d.doc_count,
            };
          });
          dataKey = 'customers';
        }
        title = TitleType.Audience;
        break;
      case REVENUE:
        newCollection = reports.revenue ? reports.revenue.collection : [];

        data = orderBy(
          flatMap(newCollection, (histogram, key) => {
            return map(histogram, (v, k) => {
              const m = moment(Number(k)).utc();
              return {
                groupBy: key,
                keyGroup: m.format('DD-MM-YYYY'),
                name: m.format(format),
                timestamp: Number(k),
                [key]: Number(parseFloat(v).toFixed(2)),
              };
            });
          }),
          'timestamp'
        );

        dataKeyGroups = Object.keys(groupBy(data, 'groupBy'));

        // Merge objects with same date
        groupedByDate = groupBy(data, 'keyGroup');
        data = reduce(
          groupedByDate,
          (acc: Array<DataItem>, value) => {
            const object = value.reduce((finalObj: DataItem, item: DataItem) => ({ ...item, ...finalObj }), {});
            return acc.concat(object);
          },
          []
        );
        title = TitleType.Revenue;
        break;
      case PAYMENTS:
        newCollection = reports.allSales ? reports.allSales.collection : [];

        if (newCollection.payments) {
          data = newCollection.payments.map((d: { doc_count: number; key: number }) => {
            return {
              name: moment(d.key).utc().format(format),
              payments: d.doc_count,
            };
          });
          dataKey = 'payments';
        }
        title = TitleType.Payments;
        break;
      case SUBSCRIPTIONS:
        newCollection = reports.subscriptions ? reports.subscriptions.collection : [];

        data = orderBy(
          flatMap(newCollection, (histogram, key) => {
            return map(histogram, (v) => {
              const m = moment(Number(v.key)).utc();
              return {
                groupBy: key,
                keyGroup: m.format('DD-MM-YYYY'),
                name: m.format(format),
                timestamp: Number(v.key),
                [key]: v.doc_count,
              };
            });
          }),
          'timestamp'
        );

        dataKeyGroups = Object.keys(groupBy(data, 'groupBy'));

        // Merge objects with same date
        groupedByDate = groupBy(data, 'keyGroup');
        data = reduce(
          groupedByDate,
          (acc: Array<DataItem>, value) => {
            const object = value.reduce((finalObj: DataItem, item: DataItem) => ({ ...item, ...finalObj }), {});
            return acc.concat(object);
          },
          []
        );
        title = TitleType.Subscriptions;
        break;
      default:
        break;
    }

    if (isFetching) {
      return <Loader />;
    }

    return (
      <>
        <Container>
          {title === TitleType.Subscriptions && <SubscriptionsInfoTile />}

          <Tile
            title={title}
            tileWidth={title === TitleType.Subscriptions ? '70%' : '100%'}
            tileContent={
              data.length > 0 ? (
                <SimpleLineChart data={data} dataKey={dataKey} dataKeyGroups={dataKeyGroups} />
              ) : (
                <EmptyMessageStyled variant="p">There is no available data for this time period.</EmptyMessageStyled>
              )
            }
          />
        </Container>
        {title === TitleType.Audience && (
          <>
            <RegistrationsSection />
            <TopInfo reportInformation={reportInformation} />
          </>
        )}
      </>
    );
  }
}
